import React, { useState, useEffect } from 'react';
import {
    Container,
    Typography,
    Box,
    Paper,
    Chip,
    Divider,
    Button,
    Skeleton,
    Alert,
    Stack
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import NewReleasesIcon from '@mui/icons-material/NewReleases';
import UpdateIcon from '@mui/icons-material/Update';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import BugReportIcon from '@mui/icons-material/BugReport';
import StarIcon from '@mui/icons-material/Star';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import FlagIcon from '@mui/icons-material/Flag';
import SpeedIcon from '@mui/icons-material/Speed';
import SecurityIcon from '@mui/icons-material/Security';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import SchoolIcon from '@mui/icons-material/School';
import CodeIcon from '@mui/icons-material/Code';
import PersonIcon from '@mui/icons-material/Person';
import api from '../services/api';

interface ChangeLogEntry {
    type: 'Correctif' | 'Fonctionnalité' | 'Amélioration' | 'Version';
    cibles: string[];
    tags: string[];
    description: string;
    photos?: string[];
    videos?: string[];
}

interface VersionEntry {
    version: string;
    date: string;
    deployDate: string;
    changeLog: ChangeLogEntry[];
}

interface PatchNoteData {
    versions: VersionEntry[];
}

const PatchNotePage = () => {
    const navigate = useNavigate();
    const [patchNotes, setPatchNotes] = useState<PatchNoteData | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const fetchPatchNotes = async () => {
            try {
                const res = await api.get('/api/system/patchnotes');
                setPatchNotes(res.data);
            } catch (err) {
                console.error('Error fetching patch notes:', err);
                setError('Impossible de charger les notes de version.');
            } finally {
                setLoading(false);
            }
        };
        fetchPatchNotes();
    }, []);

    const formatDate = (dateStr: string) => {
        const date = new Date(dateStr);
        return date.toLocaleDateString('fr-FR', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    const getChangeTypeIcon = (type: string) => {
        switch (type) {
            case 'Correctif': return <BugReportIcon fontSize="small" />;
            case 'Fonctionnalité': return <StarIcon fontSize="small" />;
            case 'Amélioration': return <AutoFixHighIcon fontSize="small" />;
            case 'Version': return <FlagIcon fontSize="small" />;
            default: return <NewReleasesIcon fontSize="small" />;
        }
    };

    const getChangeTypeColor = (type: string) => {
        switch (type) {
            case 'Correctif': return { bg: '#fee2e2', color: '#dc2626' }; // Red
            case 'Fonctionnalité': return { bg: '#dcfce7', color: '#16a34a' }; // Green
            case 'Amélioration': return { bg: '#dbeafe', color: '#2563eb' }; // Blue
            case 'Version': return { bg: '#f3e8ff', color: '#9333ea' }; // Purple
            default: return { bg: '#f3f4f6', color: '#4b5563' }; // Gray
        }
    };

    return (
        <Box sx={{
            minHeight: '100vh',
            bgcolor: '#f5f7fa',
            background: 'linear-gradient(135deg, #f5f7fa 0%, #e4e9f2 100%)',
            py: 6
        }}>
            <Container maxWidth="md">
                {/* Header / Navigation */}
                <Button
                    startIcon={<ArrowBackIcon />}
                    onClick={() => navigate(-1)}
                    sx={{
                        mb: 4,
                        textTransform: 'none',
                        color: '#555',
                        '&:hover': { bgcolor: 'rgba(0,0,0,0.05)' }
                    }}
                >
                    Retour
                </Button>

                {/* Hero Section */}
                <Paper
                    elevation={0}
                    sx={{
                        p: { xs: 4, md: 6 },
                        borderRadius: 6,
                        background: 'linear-gradient(120deg, #667eea 0%, #764ba2 100%)',
                        color: 'white',
                        mb: 4,
                        textAlign: 'center',
                        position: 'relative',
                        overflow: 'hidden',
                        boxShadow: '0 20px 40px rgba(102, 126, 234, 0.25)'
                    }}
                >
                    <Box sx={{ position: 'absolute', top: -80, left: -40, width: 250, height: 250, borderRadius: '50%', background: 'rgba(255,255,255,0.1)', filter: 'blur(50px)' }} />
                    <Box sx={{ position: 'absolute', bottom: -40, right: -40, width: 180, height: 180, borderRadius: '50%', background: 'rgba(255,255,255,0.1)', filter: 'blur(40px)' }} />

                    <NewReleasesIcon sx={{ fontSize: 56, mb: 2, opacity: 0.9 }} />
                    <Typography variant="h3" component="h1" sx={{ fontWeight: 800, mb: 1, fontFamily: 'Quicksand, sans-serif' }}>
                        Notes de Version
                    </Typography>
                    <Typography variant="h6" sx={{ opacity: 0.9, fontWeight: 400 }}>
                        Découvrez les dernières nouveautés et améliorations
                    </Typography>
                </Paper>

                {/* Error State */}
                {error && (
                    <Alert severity="error" sx={{ mb: 4, borderRadius: 3 }}>
                        {error}
                    </Alert>
                )}

                {/* Loading State */}
                {loading && (
                    <Box>
                        {[1, 2].map((i) => (
                            <Paper key={i} sx={{ p: 4, mb: 3, borderRadius: 4 }}>
                                <Skeleton variant="text" width="30%" height={40} />
                                <Skeleton variant="text" width="50%" height={24} sx={{ mb: 2 }} />
                                <Skeleton variant="rectangular" height={100} sx={{ borderRadius: 2 }} />
                            </Paper>
                        ))}
                    </Box>
                )}

                {/* Patch Notes List */}
                {!loading && patchNotes && patchNotes.versions.map((version, index) => (
                    <Paper
                        key={version.version}
                        elevation={0}
                        sx={{
                            p: { xs: 3, md: 4 },
                            mb: 3,
                            borderRadius: 4,
                            border: index === 0 ? '2px solid #667eea' : '1px solid rgba(0,0,0,0.08)',
                            bgcolor: 'white',
                            transition: 'all 0.3s ease',
                            '&:hover': {
                                boxShadow: '0 8px 24px rgba(0,0,0,0.1)',
                                transform: 'translateY(-2px)'
                            }
                        }}
                    >
                        {/* Version Header */}
                        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2, mb: 3 }}>
                            <Box sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                bgcolor: index === 0 ? '#667eea' : '#e0e0e0',
                                color: index === 0 ? 'white' : '#555',
                                px: 2,
                                py: 0.75,
                                borderRadius: 3,
                                fontWeight: 700
                            }}>
                                <UpdateIcon sx={{ fontSize: 18 }} />
                                v{version.version}
                            </Box>
                            {index === 0 && (
                                <Chip
                                    label="Dernière version"
                                    size="small"
                                    sx={{
                                        bgcolor: '#dcfce7',
                                        color: '#15803d',
                                        fontWeight: 600
                                    }}
                                />
                            )}
                            <Box sx={{ flex: 1 }} />
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'text.secondary', fontSize: 14 }}>
                                <CalendarTodayIcon sx={{ fontSize: 16 }} />
                                {formatDate(version.deployDate)}
                            </Box>
                        </Box>

                        <Divider sx={{ mb: 3 }} />

                        {/* Changelog Items */}
                        <Stack spacing={2}>
                            {version.changeLog.map((change, idx) => {
                                const style = getChangeTypeColor(change.type);
                                return (
                                    <Box
                                        key={idx}
                                        sx={{
                                            p: 2,
                                            borderRadius: 3,
                                            bgcolor: 'rgba(0,0,0,0.02)',
                                            border: '1px solid rgba(0,0,0,0.03)',
                                            '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' },
                                            transition: 'background-color 0.2s'
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'start', gap: 2 }}>
                                            {/* Icon */}
                                            <Box sx={{
                                                mt: 0.5,
                                                p: 1,
                                                borderRadius: '50%',
                                                bgcolor: style.bg,
                                                color: style.color,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center'
                                            }}>
                                                {getChangeTypeIcon(change.type)}
                                            </Box>

                                            {/* Content */}
                                            <Box sx={{ flex: 1 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                                                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#2d3748' }}>
                                                        {change.type}
                                                    </Typography>

                                                    {/* Target Chips */}
                                                    {change.cibles && change.cibles.map(target => {
                                                        const getTargetConfig = (t: string) => {
                                                            switch (t) {
                                                                case 'Admin': return {
                                                                    icon: <AdminPanelSettingsIcon sx={{ fontSize: 14 }} />,
                                                                    bg: '#eef2ff', color: '#4f46e5', border: '#c7d2fe'
                                                                };
                                                                case 'Formateur': return {
                                                                    icon: <SchoolIcon sx={{ fontSize: 14 }} />,
                                                                    bg: '#f0fdf4', color: '#16a34a', border: '#bbf7d0'
                                                                };
                                                                case 'Dev': return {
                                                                    icon: <CodeIcon sx={{ fontSize: 14 }} />,
                                                                    bg: '#fdf4ff', color: '#c026d3', border: '#f5d0fe'
                                                                };
                                                                case 'Apprenant': return {
                                                                    icon: <PersonIcon sx={{ fontSize: 14 }} />,
                                                                    bg: '#fff7ed', color: '#ea580c', border: '#fed7aa'
                                                                };
                                                                case 'Sécurité': return {
                                                                    icon: <SecurityIcon sx={{ fontSize: 14 }} />,
                                                                    bg: '#fef2f2', color: '#dc2626', border: '#fecaca'
                                                                };
                                                                default: return {
                                                                    icon: undefined,
                                                                    bg: 'white', color: '#4a5568', border: '#cbd5e0'
                                                                };
                                                            }
                                                        };
                                                        const config = getTargetConfig(target);
                                                        return (
                                                            <Chip
                                                                key={target}
                                                                icon={config.icon}
                                                                label={target}
                                                                size="small"
                                                                sx={{
                                                                    height: 24,
                                                                    fontSize: '0.75rem',
                                                                    fontWeight: 600,
                                                                    bgcolor: config.bg,
                                                                    color: config.color,
                                                                    border: `1px solid ${config.border}`,
                                                                    '& .MuiChip-icon': {
                                                                        color: config.color,
                                                                        ml: 0.5
                                                                    }
                                                                }}
                                                            />
                                                        );
                                                    })}
                                                </Box>

                                                <Typography variant="body2" sx={{ color: '#4a5568', lineHeight: 1.6, mb: 1.5 }}>
                                                    {change.description}
                                                </Typography>

                                                {/* Tags */}
                                                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                                    {change.tags && change.tags.map(tag => (
                                                        <Chip
                                                            key={tag}
                                                            label={`#${tag}`}
                                                            size="small"
                                                            sx={{
                                                                height: 20,
                                                                fontSize: '0.7rem',
                                                                bgcolor: 'white',
                                                                border: '1px solid #e2e8f0',
                                                                color: '#718096'
                                                            }}
                                                        />
                                                    ))}
                                                </Box>

                                                {/* Media Section */}
                                                {(change.photos?.length || 0) > 0 && (
                                                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 1.5, mt: 2 }}>
                                                        {change.photos!.map((photo, pIdx) => (
                                                            <Box
                                                                key={pIdx}
                                                                component="img"
                                                                src={photo}
                                                                alt={`Illustration ${pIdx + 1}`}
                                                                loading="lazy"
                                                                sx={{
                                                                    height: 150,
                                                                    borderRadius: 2,
                                                                    cursor: 'pointer',
                                                                    border: '1px solid rgba(0,0,0,0.1)',
                                                                    transition: 'transform 0.2s',
                                                                    '&:hover': { transform: 'scale(1.02)' }
                                                                }}
                                                                onClick={() => window.open(photo, '_blank')}
                                                            />
                                                        ))}
                                                    </Box>
                                                )}

                                                {(change.videos?.length || 0) > 0 && (
                                                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 1.5, mt: 2 }}>
                                                        {change.videos!.map((video, vIdx) => (
                                                            <Box
                                                                key={vIdx}
                                                                component="video"
                                                                controls
                                                                src={video}
                                                                sx={{
                                                                    height: 200,
                                                                    maxWidth: '100%',
                                                                    borderRadius: 2,
                                                                    border: '1px solid rgba(0,0,0,0.1)'
                                                                }}
                                                            />
                                                        ))}
                                                    </Box>
                                                )}
                                            </Box>
                                        </Box>
                                    </Box>
                                );
                            })}
                        </Stack>
                    </Paper>
                ))}

                {/* Empty State */}
                {!loading && (!patchNotes || patchNotes.versions.length === 0) && !error && (
                    <Paper sx={{ p: 6, textAlign: 'center', borderRadius: 4 }}>
                        <Typography color="text.secondary">
                            Aucune note de version disponible.
                        </Typography>
                    </Paper>
                )}
            </Container>
        </Box>
    );
};

export default PatchNotePage;
